import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import PasswordField from 'components/PasswordField';
import validations from 'validations';
import { Button } from 'ncoded-component-library';
import AgreeToTcField from '../AgreeToTcField';
import { ShareableLinkMainInfo } from 'models/ShareableLink';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import CardWrapper from '../CardWrapper';

import './ShareableLinkPassword.styles.scss';
import './ShareableLinkPassword.styles.responsive.scss';

type ShareableLinkPasswordProps = {
  className?: string;
  shareableLinkId: string;
  requestedPasswordAt: Date;
  termsAndConditionsContent: string;
  hasNDA: boolean;
  updateMainInfo: (mainInfo: ShareableLinkMainInfo) => void;
  onPasswordConfirmed: () => void;
};

const ShareableLinkPassword: React.FC<ShareableLinkPasswordProps> = (props) => {
  const {
    className,
    shareableLinkId,
    requestedPasswordAt,
    termsAndConditionsContent,
    hasNDA,
    updateMainInfo,
    onPasswordConfirmed,
  } = props;

  const [hasRequestOrReminderSent, setHasRequestOrReminderSent] =
    useState(false);

  const hasAlreadyRequestedAccess = useMemo(
    () => !!requestedPasswordAt,
    [requestedPasswordAt],
  );

  const classes = classNames('shareable-link-password', className);

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const { currentUser } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const onSendReminder = useCallback(async () => {
    try {
      setHasRequestOrReminderSent(true);
      await api.shareableLinks.requestPasswordReminder(shareableLinkId);
      popSuccess(t('yourPasswordRequestubmittedSuccessfully'));
    } catch (e) {
      popServerError(e);
      setHasRequestOrReminderSent(false);
    }
  }, [shareableLinkId, popSuccess, t, popServerError]);

  const onRequestPassword = useCallback(async () => {
    try {
      setHasRequestOrReminderSent(true);
      await api.shareableLinks.requestPassword(
        shareableLinkId,
        currentUser?.email,
      );
      popSuccess(t('yourPasswordRequestubmittedSuccessfully'));
    } catch (e) {
      popServerError(e);
      setHasRequestOrReminderSent(false);
    }
  }, [shareableLinkId, currentUser?.email, popSuccess, t, popServerError]);

  const onSubmit = useCallback(
    async (values: { password: string; agreeToTC?: boolean }) => {
      const { password, agreeToTC } = values;

      try {
        const { data } = await api.shareableLinks.confirmPassword(
          shareableLinkId,
          password,
          agreeToTC,
        );
        updateMainInfo(data);
        onPasswordConfirmed();
      } catch (e) {
        popServerError(e);
      }
    },
    [shareableLinkId, updateMainInfo, onPasswordConfirmed, popServerError],
  );

  return (
    <div className={classes}>
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            <CardWrapper hasNDA={hasNDA}>
              <div className="password-wrapper">
                <p>{t('accessPassword')}</p>
                <p>{t('accessPasswordSecureLink')}</p>
                <Field
                  name="password"
                  component={PasswordField}
                  required
                  validate={validations.required()}
                  withoutStrengthIndicator
                />
                <Button
                  variant="link"
                  disabled={hasRequestOrReminderSent}
                  onClick={
                    hasAlreadyRequestedAccess
                      ? onSendReminder
                      : onRequestPassword
                  }
                >
                  {t('requestAccessPassword')}
                </Button>
              </div>

              {termsAndConditionsContent && (
                <div>
                  <AgreeToTcField
                    termsAndConditionsContent={termsAndConditionsContent}
                  />
                </div>
              )}

              <Button type="submit" disabled={submitting}>
                {t('continue')}
              </Button>
            </CardWrapper>
          </form>
        )}
      />
    </div>
  );
};

export default ShareableLinkPassword;
